import React from 'react';
import { 
  Box, Grid, Paper, Typography, Button, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar, Chip, useTheme, Divider 
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowTrendUp, faWallet, faStore, faCrown, 
  faCircleCheck, faClock 
} from '@fortawesome/free-solid-svg-icons';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell 
} from 'recharts';

// --- DATOS MOCK ---
const REVENUE_HISTORY = [
  { month: 'Ene', b2b: 4000, b2c: 2400 },
  { month: 'Feb', b2b: 4500, b2c: 3800 },
  { month: 'Mar', b2b: 4200, b2c: 4100 },
  { month: 'Abr', b2b: 5800, b2c: 5900 },
  { month: 'May', b2b: 6100, b2c: 8200 },
];

const REVENUE_MIX = [
  { name: 'SaaS B2B', value: 6100, color: '#0F172A' }, // Slate 900
  { name: 'QUD Pass', value: 8200, color: '#2563EB' }, // Blue 600
];

export default function AdminHome() {
  const theme = useTheme();

  return (
    <Box sx={{ pb: 4 }}>
      
      {/* HEADER MACRO */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#111', letterSpacing: -0.5 }}>
            Visión General
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Resumen del rendimiento macro de QUD Platform.
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          disableElevation 
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3, bgcolor: '#000', '&:hover': { bgcolor: '#222'} }}
        >
          Generar Reporte
        </Button>
      </Box>

      {/* KPI ROW: Minimalist cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Volumen Total', value: '$14,300', icon: faWallet, trend: '+12.5%', color: '#2563EB' },
          { label: 'Comercios SaaS', value: '142', icon: faStore, trend: '+4%', color: '#0F172A' },
          { label: 'Usuarios Pass', value: '854', icon: faCrown, trend: '+28%', color: '#F59E0B' },
        ].map((kpi, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #EAEAEA', display: 'flex', alignItems: 'center', gap: 3 }}>
              <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: '#F8F9FA', display: 'flex', justifyContent: 'center', alignItems: 'center', color: kpi.color }}>
                <FontAwesomeIcon icon={kpi.icon} />
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: '#888', fontWeight: 700, textTransform: 'uppercase' }}>{kpi.label}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>{kpi.value}</Typography>
                  <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 700 }}>{kpi.trend}</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* CHARTS ROW */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #EAEAEA' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 3 }}>Crecimiento por Segmento</Typography>
            <Box sx={{ height: 300, width: '100%' }}>
              <ResponsiveContainer>
                <AreaChart data={REVENUE_HISTORY}>
                  <defs>
                    <linearGradient id="colorB2C" x1="0" y1="0" x2="0" y2="100%">
                      <stop offset="5%" stopColor="#2563EB" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#2563EB" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F0F0F0" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#AAA'}} />
                  <YAxis hide />
                  <Tooltip 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                  />
                  <Area type="monotone" dataKey="b2c" stroke="#2563EB" strokeWidth={3} fillOpacity={1} fill="url(#colorB2C)" />
                  <Area type="monotone" dataKey="b2b" stroke="#0F172A" strokeWidth={3} fill="transparent" strokeDasharray="5 5" />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid #EAEAEA', height: '100%' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 2 }}>Mix de Ingresos</Typography>
            <Box sx={{ height: 200 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={REVENUE_MIX} innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                    {REVENUE_MIX.map((entry, index) => <Cell key={index} fill={entry.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
            <Box sx={{ mt: 2 }}>
              {REVENUE_MIX.map((item, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#666' }}>{item.name}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800 }}>${item.value}</Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* RECENT ACTIVITY TABLE */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #EAEAEA', overflow: 'hidden' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #EAEAEA', bgcolor: '#FAFAFA' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Actividad Reciente de la Red</Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#FBFBFB' }}>
                <TableCell sx={{ color: '#888', fontWeight: 700, fontSize: '0.7rem' }}>ENTIDAD</TableCell>
                <TableCell sx={{ color: '#888', fontWeight: 700, fontSize: '0.7rem' }}>ACCIÓN</TableCell>
                <TableCell sx={{ color: '#888', fontWeight: 700, fontSize: '0.7rem' }}>FECHA</TableCell>
                <TableCell align="right" sx={{ color: '#888', fontWeight: 700, fontSize: '0.7rem' }}>ESTADO</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[
                { name: 'Gimnasio IronHouse', action: 'Registro de Comercio', date: 'Hace 5 min', status: 'Verificado', icon: faCircleCheck, color: '#10B981' },
                { name: 'Carlos Pérez', action: 'Compra QUD Pass', date: 'Hace 12 min', status: 'Completado', icon: faCircleCheck, color: '#10B981' },
                { name: 'Restaurante El Faro', action: 'Solicitud Payout', date: 'Hace 45 min', status: 'Pendiente', icon: faClock, color: '#F59E0B' },
              ].map((row, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{row.name}</TableCell>
                  <TableCell sx={{ color: '#666', fontSize: '0.85rem' }}>{row.action}</TableCell>
                  <TableCell sx={{ color: '#AAA', fontSize: '0.8rem' }}>{row.date}</TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={row.status} 
                      size="small" 
                      sx={{ fontSize: '0.65rem', fontWeight: 900, bgcolor: `${row.color}15`, color: row.color, borderRadius: 1 }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}